import clsx from 'clsx';
import React, { useEffect, useMemo, useRef } from 'react';
import { usePopper } from 'react-popper';
import { useFocusOnContext } from '../_internal/hooks';
import { OverrideButtonsContainer } from '../button/regular-button';
import { Portal } from '../portal';
import { popperModifiers } from '../utils';
import { HintProvider } from './hint-provider';
import cssStyles from './styles/hint.module.css';
/**
 * `<Hint />` - Компонент позволяющий показывать пользователю дополнительную информацию.
 *
 * @example ``` tsx
 <Hint
  open={isOpen}
  anchor={<RegularButton onClick={() => setIsOpen(!isOpen)}>Click for Hint</Button>}>
    <HintContent header="Your header" paragraph="Your paragraph" />
 </Hint>
 * ```
 */
export const Hint = ({
  className,
  popoverClassName,
  open,
  closeButton,
  placement = 'top',
  actionButton,
  extraButton,
  anchor,
  children
}) => {
  const anchorRef = useRef(null);
  const hintRef = useRef(null);
  const arrowRef = useRef(null);
  const focusOnContext = useFocusOnContext();
  const {
    styles,
    attributes,
    update
  } = usePopper(anchorRef.current, hintRef.current, {
    placement,
    modifiers: [{
      name: 'arrow',
      options: {
        element: arrowRef.current,
        padding: 9
      }
    }, ...popperModifiers({
      listeners: open
    })]
  });
  useEffect(() => {
    update == null ? void 0 : update();
  }, [update, anchor]);
  const wrapperClassName = useMemo(() => clsx(cssStyles.wrapper, className), [className]);
  const hintClassName = useMemo(() => clsx(popoverClassName, cssStyles.hint, {
    [cssStyles.disabled]: !open,
    [cssStyles.inModal]: !!focusOnContext
  }), [popoverClassName, focusOnContext, open]);
  const hasButton = actionButton || extraButton;
  const isOnlyExtraButton = !!extraButton && !actionButton;
  return /*#__PURE__*/React.createElement("div", {
    className: wrapperClassName
  }, /*#__PURE__*/React.cloneElement(anchor, {
    ref: anchorRef
  }), /*#__PURE__*/React.createElement(Portal, null, /*#__PURE__*/React.createElement("div", {
    className: hintClassName,
    ref: hintRef,
    style: styles.popper,
    role: "tooltip",
    ...attributes.popper
  }, children, (closeButton || hasButton) && (/*#__PURE__*/React.createElement(HintProvider, {
    value: {
      open
    }
  }, closeButton, hasButton && (/*#__PURE__*/React.createElement("div", {
    className: cssStyles.buttonBlock
  }, /*#__PURE__*/React.createElement(OverrideButtonsContainer, {
    hard: {
      className: clsx({
        [cssStyles.onlyExtraButton]: isOnlyExtraButton,
        [cssStyles.extraButton]: !isOnlyExtraButton
      })
    }
  }, extraButton), actionButton)))), /*#__PURE__*/React.createElement("div", {
    className: cssStyles.arrow,
    ref: arrowRef,
    style: styles.arrow
  }))));
};